import type { Router, RouteRecordRaw } from 'vue-router'
import { PROMOTION_ACTIVITY_LIST_PATH } from '@/hooks/web/usePromotionActivityNav'

/**
 * 营销中心独立窗隐藏页挂载约定
 *
 * 活动列表在后台菜单 `/ai-Application-center/promotion/activity/activity-list`；
 * 编辑 / 详情 / 预览不进侧栏，必须挂到同一 Layout 根，否则独立窗点编辑会 404 或丢菜单。
 */

type PromotionHiddenChild = {
  /** 相对 /ai-Application-center/promotion 的 path */
  path: string
  /** 全局唯一的路由 name */
  name: string
  component: RouteRecordRaw['component']
  meta: Record<string, unknown>
}

const PROMOTION_ROOT_PATH = '/ai-Application-center/promotion'

const PROMOTION_ACTIVITY_ACTIVE_MENU = `${PROMOTION_ROOT_PATH}${PROMOTION_ACTIVITY_LIST_PATH}`

const PROMOTION_HIDDEN_CHILDREN: PromotionHiddenChild[] = [
  {
    path: 'activity/edit/:id?',
    name: 'PromotionActivityEditStandalone',
    component: () => import('@/views/promotion/activity/PortalEditorPage.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      title: '活动编辑',
      activeMenu: PROMOTION_ACTIVITY_ACTIVE_MENU,
      moduleCode: 'promotion'
    }
  },
  {
    path: 'activity/detail/:id(\\d+)',
    name: 'PromotionActivityDetailStandalone',
    component: () => import('@/views/promotion/activity/Detail.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      title: '活动详情',
      activeMenu: PROMOTION_ACTIVITY_ACTIVE_MENU,
      moduleCode: 'promotion'
    }
  },
  {
    path: 'activity/preview/:id(\\d+)',
    name: 'PromotionActivityPreviewStandalone',
    component: () => import('@/views/promotion/activity/Preview.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      title: '活动预览',
      activeMenu: PROMOTION_ACTIVITY_ACTIVE_MENU,
      moduleCode: 'promotion'
    }
  }
]

const registeredNames = new Set<string>()

/**
 * 后台 /ai-Application-center/promotion 菜单树就绪后调用：
 * 把详情/隐藏页挂到与列表相同的 Layout 父级。
 */
export function ensurePromotionHiddenRoutes(router: Router) {
  for (const name of [...registeredNames]) {
    if (router.hasRoute(name)) router.removeRoute(name)
  }
  registeredNames.clear()

  const parents = router
    .getRoutes()
    .filter((route) => route.path === PROMOTION_ROOT_PATH && route.name)

  for (const parent of parents) {
    for (const def of PROMOTION_HIDDEN_CHILDREN) {
      if (router.hasRoute(def.name)) router.removeRoute(def.name)
      router.addRoute(String(parent.name), {
        path: def.path,
        name: def.name,
        component: def.component,
        meta: { ...def.meta }
      })
      registeredNames.add(def.name)
    }
  }
}

/** 兼容旧调用名（避免 HMR/缓存仍引用 ensurePromotionLinkRoutes 导致白屏） */
export const ensurePromotionLinkRoutes = ensurePromotionHiddenRoutes
